import { inject, Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { BehaviorSubject } from "rxjs";
import { v4 as uuid } from "uuid";
import { MESSAGING_CONFIG } from "@app/config/messaging.config";
import { StorageService } from "@app/services/storage.service";
import { TokenManagerService } from "@app/services/token-manager.service";
import { MESSAGING_PROVIDER } from "./messaging-provider.token";
import { MESSAGING_LIFECYCLE } from "./messaging-lifecycle.token";
import { PushPermission } from "./messaging.types";
import { PushRegistrationService } from "./push-registration.service";
import { environment } from "../../../environments/environment";
import { EnvironmentType } from "@app/models/env";

@Injectable({ providedIn: "root" })
export class MessagingService {
  private readonly provider = inject(MESSAGING_PROVIDER);
  private readonly onResume = inject(MESSAGING_LIFECYCLE);
  private readonly config = inject(MESSAGING_CONFIG);
  private readonly storage = inject(StorageService);
  private readonly registration = inject(PushRegistrationService);
  private readonly auth = inject(TokenManagerService);
  private readonly permission = new BehaviorSubject<PushPermission>("prompt");
  readonly permission$ = this.permission.asObservable();
  readonly received$ = this.provider.received$;
  readonly opened$ = this.provider.opened$;
  private initialization?: Promise<void>;
  private installationId?: string;
  private token?: string;
  private userId?: string;
  private previousBearer?: string;
  private lastSaved?: string;
  private sessionRevision = 0;
  private queue: Promise<void> = Promise.resolve();
  private permissionRequest?: Promise<PushPermission>;
  private resumeListenerAttached = false;

  constructor() {
    this.provider.token$.subscribe(token => {
      this.token = token;
      this.debug("token recebido/renovado");
      void this.sync();
    });
    this.received$.subscribe(message => this.debug("notificação recebida", message));
    this.opened$.subscribe(action => this.debug("notificação aberta", action));
  }

  private debug(event: string, details?: unknown): void {
    if (!this.config.debug || environment === EnvironmentType.production) return;
    // A serialized snapshot can be copied from DevTools or native logs. Never include auth credentials.
    console.log(`[Push/FCM] ${event}`, JSON.stringify({
      timestamp: new Date().toISOString(), environment,
      provider: this.provider.name, platform: Capacitor.getPlatform(),
      installationId: this.installationId ?? null, token: this.token ?? null,
      permission: this.permission.value,
      authenticated: !!this.userId && !!this.auth.accessToken,
      ...(details === undefined ? {} : { details }),
    }, null, 2));
  }

  initialize(): Promise<void> {
    if (!this.config.enabled || !Capacitor.isNativePlatform()) {
      this.debug("inicialização ignorada (push desativado ou plataforma web)");
      return Promise.resolve();
    }
    return this.initialization ??= this.start().catch(error => {
      this.initialization = undefined;
      this.debug("falha na inicialização; nova tentativa ao retomar");
      throw error;
    });
  }

  private async start(): Promise<void> {
    if (!this.resumeListenerAttached) {
      await this.onResume(() => {
        void this.initialize().then(() => this.refreshPermission(false))
          .catch(() => console.warn("[Push] Falha ao atualizar registro"));
      });
      this.resumeListenerAttached = true;
    }
    this.installationId = await this.storage.get("push.installationId") || uuid();
    await this.storage.set("push.installationId", this.installationId);
    this.debug("instalação identificada");
    await this.provider.initialize();
    this.debug("listeners nativos registrados");
    if (this.config.requestPermissionOnStartup) await this.requestPermission();
    else await this.refreshPermission(false);
  }

  requestPermission(): Promise<PushPermission> {
    if (!this.config.enabled || !Capacitor.isNativePlatform()) return Promise.resolve("denied");
    return this.permissionRequest ??= this.refreshPermission(true).finally(() => { this.permissionRequest = undefined; });
  }

  async checkPermission(): Promise<PushPermission> {
    if (!this.config.enabled || !Capacitor.isNativePlatform()) return "denied";
    return this.provider.checkPermission();
  }

  private async refreshPermission(request: boolean): Promise<PushPermission> {
    let permission = await this.provider.checkPermission();
    if (request && (permission === "prompt" || permission === "prompt-with-rationale")) {
      permission = await this.provider.requestPermission();
    }
    this.permission.next(permission);
    this.debug("permissão consultada");
    if (permission === "granted") {
      // APNs registration may not yet have completed on first launch; retry on resume.
      try { this.token = await this.provider.getToken(); }
      catch {
        this.debug("token indisponível; nova tentativa ao retomar");
        console.warn("[Push] Token ainda indisponível; nova tentativa ao retomar o app");
      }
    }
    // Logged before backend synchronization, including when no user is logged in.
    this.debug("estado local para teste");
    await this.sync();
    return permission;
  }

  /** Called for login, restored sessions, account switches and logout. Never blocks authentication. */
  setUser(userId?: string): void {
    const previous = this.userId;
    const bearer = this.previousBearer;
    if (previous !== userId) {
      this.sessionRevision++;
      this.lastSaved = undefined;
    }
    this.userId = userId;
    this.previousBearer = this.auth.accessToken || undefined;
    if (previous && previous !== userId && bearer && this.installationId) {
      const installationId = this.installationId;
      this.enqueue(async () => { await this.registration.remove(installationId, bearer); });
    }
    void this.sync();
  }

  /** Complete removal with the current bearer before the logout endpoint revokes it. */
  async clearUser(): Promise<void> {
    this.setUser(undefined);
    await this.queue;
  }

  sync(): Promise<void> {
    return this.enqueue(async () => {
      const bearer = this.auth.accessToken;
      if (!this.userId || !bearer || !this.token || !this.installationId) return;
      const permission = this.permission.value;
      const revision = this.sessionRevision;
      const key = `${this.userId}:${this.token}:${permission}`;
      if (key === this.lastSaved) return;
      this.previousBearer = bearer;
      this.debug("enviando cadastro ao backend");
      await this.registration.save({
        installationId: this.installationId, provider: this.provider.name,
        token: this.token, platform: Capacitor.getPlatform(), permission,
      }, bearer);
      this.debug("cadastro aceito pelo backend");
      if (revision === this.sessionRevision) this.lastSaved = key;
    });
  }

  private enqueue(work: () => Promise<void>): Promise<void> {
    this.queue = this.queue.then(work).catch(() => {
      this.debug("falha na sincronização com backend");
      // Never log the HTTP error object: it may contain auth headers. Resume/login retries registration.
      console.warn("[Push] Registro não sincronizado; confira conexão e contrato do backend");
    });
    return this.queue;
  }
}
